import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowUpCircle,
  ArrowDownCircle,
  Banknote,
  MoreVertical,
  ChevronRight,
  Search,
} from "lucide-react";
import { useSelector } from "react-redux";
import { getWalletStoreOwnerCustomerDetails } from "../../services/commonServices";
import { useParams } from "react-router-dom";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import { formatCurrencyINR } from "../../utils/function";
import { useTranslation } from "react-i18next";

const StoreOwnerWalletDetails = () => {
  let auth = useSelector((x) => x.auth.value);
  const { uuid } = useParams();
  const { t } = useTranslation();

  const [transactionsDetails, setTransactionsDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!uuid) return;
    setIsLoading(true);
    const data = {
      store_id: auth.active_store_id,
      customer_id: uuid,
    };
    getWalletStoreOwnerCustomerDetails(data)
      .then((response) => {
        const decrypt = decryptData(response.data.data);
        setTransactionsDetails(decrypt.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        notify.error(Utils.getErrorMessage(err));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [auth.active_store_id, uuid]);

  const filteredTransactions = transactionsDetails?.filter((item) =>
    item?.order_number?.toString().toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="p-4 md:p-8 bg-gray-50 dark:bg-slate-950 min-h-screen font-sans transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-500 font-semibold text-sm mb-4 transition-colors group"
          >
            <div className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 group-hover:border-orange-100 dark:group-hover:border-orange-900/50 group-hover:bg-orange-50 dark:group-hover:bg-orange-950/30 transition-all">
              <ArrowLeft size={18} />
            </div>
            {t("backToWallet")}
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 dark:text-slate-100 tracking-tight">
                {t("transactionHistory")}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                {t("detailedCashFlow")}
              </p>
            </div>

            {/* Desktop Search Bar */}
            <div className="relative hidden md:block">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                size={18}
              />
              <input
                type="text"
                placeholder={t("searchByOrderID")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm text-slate-800 dark:text-slate-200 focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none w-72 transition-all shadow-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div className="hidden md:block">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("type")}
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("orderDetails")}
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("dateTime")}
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("method")}
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {t("amount")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {filteredTransactions?.length > 0 ? (
                  filteredTransactions?.map((item, index) => {
                    const type = Number(item?.type); // 1 = IN, 2 = OUT
                    const amount = Number(item?.amount || 0);

                    return (
                      <tr
                        key={index}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group"
                      >
                        {/* ICON */}
                        <td className="px-8 py-4">
                          <div
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105 ${
                              type === 1
                                ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                                : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                            }`}
                          >
                            {type === 1 ? (
                              <ArrowUpCircle size={24} />
                            ) : (
                              <ArrowDownCircle size={24} />
                            )}
                          </div>
                        </td>

                        {/* ORDER */}
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5">
                            <span className="font-extrabold text-slate-800 dark:text-slate-200 text-[15px] tracking-tight">
                              #{item.order_number}
                            </span>

                            <div className="flex">
                              <span
                                className={`${
                                  type === 1 ? "bg-green-500" : "bg-red-500"
                                } text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm uppercase tracking-wider`}
                              >
                                {item.type_name}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* DATE */}
                        <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 font-medium">
                          {item.created_at}
                        </td>

                        {/* METHOD */}
                        <td className="px-6 py-4">
                          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300 font-bold text-[11px] uppercase">
                            <Banknote
                              size={14}
                              className="text-slate-400 dark:text-slate-500"
                            />
                            {item.method_name}
                          </div>
                        </td>

                        {/* AMOUNT */}
                        <td className="px-6 py-4">
                          <span
                            className={`text-lg font-black ${
                              type === 1
                                ? "text-green-600 dark:text-green-400"
                                : type === 2
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-gray-500 dark:text-slate-500"
                            }`}
                          >
                            {`${type === 1 ? "+" : type === 2 ? "-" : ""}${formatCurrencyINR(
                              Math.abs(amount),
                            )}`}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-10 text-slate-400 dark:text-slate-600 font-medium"
                    >
                      {t("noTransactionsFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden">
            <div className="p-5 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-800/30">
              <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm uppercase tracking-wide">
                {t("recentRecords")}
              </h3>
              <span className="text-[10px] bg-orange-500 text-white px-2 py-1 rounded-lg font-black shadow-sm">
                {transactionsDetails?.length} {t("total")}
              </span>
            </div>
            <div className="divide-y divide-slate-50 dark:divide-slate-800 px-2">
              {transactionsDetails?.length > 0 ? (
                transactionsDetails?.map((item) => {
                  const type = (item?.type_name || "").toLowerCase();
                  const amount = Number(item?.amount || 0);

                  return (
                    <div
                      key={item.id}
                      className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-2xl transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${
                            type === "in"
                              ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                              : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                          }`}
                        >
                          {type === "in" ? (
                            <ArrowUpCircle size={26} />
                          ) : (
                            <ArrowDownCircle size={26} />
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              #{item.order_number}
                            </span>
                            <span
                              className={`${type === "in" ? "bg-green-500" : "bg-red-500"} text-white text-[8px] px-1.5 py-0.5 rounded font-black uppercase shadow-sm`}
                            >
                              {item.type_name}
                            </span>
                          </div>

                          <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium mt-1">
                            {item.created_at}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p
                          className={`font-black text-base ${
                            type === "in"
                              ? "text-green-600 dark:text-green-400"
                              : type === "out"
                                ? "text-red-600 dark:text-red-400"
                                : "text-gray-500 dark:text-slate-500"
                          }`}
                        >
                          {`${type === "in" ? "+" : type === "out" ? "-" : ""}${formatCurrencyINR(
                            Math.abs(amount),
                          )}`}
                        </p>

                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">
                          {item.method_name}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400 dark:text-slate-600">
                  <p className="font-semibold text-slate-500 dark:text-slate-400">
                    {t("noTransactionsFound")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreOwnerWalletDetails;
